import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useMemo, useCallback, useLayoutEffect, cloneElement } from 'react';
import classnames from 'classnames';
import { throttle } from '@hh.ru/magritte-common-func-utils';
import { useResize } from '@hh.ru/magritte-common-resize';
import { BottomSheet } from '@hh.ru/magritte-ui-bottom-sheet';
import { BreadcrumbsDivider } from './BreadcrumbsDivider.js';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Card } from '@hh.ru/magritte-ui-card';
import { Drop } from '@hh.ru/magritte-ui-drop';
import { Text } from '@hh.ru/magritte-ui-typography';
import { s as styles } from './breadcrumbs-4VR4WruV.js';

const MAX_VISIBLE_ITEMS = 5;
const TEXT_TYPOGRAPHY = 'label-3-regular';
const arrayFromRange = (start, end) => {
    return start <= end ? [...Array(end - start + 1).keys()].map((x) => x + start) : [];
};
const makeItemKey = (children, index) => {
    return (children[index].key ??
        (typeof children[index].props.children === 'string' ? children[index].props.children : index));
};
/**
 * В спецификации указано, что breadcrumbs не может отображать более 5-ти элементов.
 *
 * Варианты отображения breadcrumbs:
 * -> link / link / link / link / text
 * или
 * -> link / ellipsis / link / link / text
 *
 * Если лимит будет превышен, компонент схлопнет в ellipsis все элементы, которые окажутся между первым и
 * тремя последними элементами. Таким образом breadcrumbs будет приведен к виду -> link / ellipsis / link / link / text,
 * где ellipsis — это кнопка, раскрывающая дроп-контейнер, в который складываются сколлапсировавшие элементы.
 *
 * Если после лимитирования контейнер-родитель всё равно не будет умещать в себе контент, то видимые элементы
 * один за другим начнут коллапсировать до максимально возможного уровеня сворачивания элементов:
 * -> link / ellipsis / text.
 *
 * Если и после этого контент все равно будет слишком широким, первый элемент начнет уменьшаться с text-overflow:ellipsis.
 *
 * Коллапсирование реализовано стилями, layout effect только вычисляет, какие элементы нужно показывать в дроп-контейнере,
 * и прячет / показывает ellipsis с помощью `visibility:hidden`, не влияя на размеры чего-либо в верстке.
 */
const Breadcrumbs = ({ children, 'data-qa': dataQa }) => {
    const activatorRef = useRef(null);
    const containerRef = useRef(null);
    const [firstCollapsedItemIndex, setFirstCollapsedItemIndex] = useState(null);
    const [lastCollapsedItemIndex, setLastCollapsedItemIndex] = useState(null);
    const showEllipsis = firstCollapsedItemIndex !== null && lastCollapsedItemIndex !== null;
    const [expandCollapsedItems, setExpandCollapsedItems] = useState(false);
    const itemRefs = useRef({});
    const itemRefCallbacks = useMemo(() => [...Array(children.length).keys()].map((_, index) => (element) => {
        itemRefs.current[index] = element;
    }), [children.length]);
    const { isMobile } = useBreakpoint();
    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const checkIfItemsCollapsed = useCallback(throttle(() => {
        const collapsedItemIndexes = Object.keys(itemRefs.current).filter((index) => itemRefs.current[index]?.offsetTop > 0);
        const _firstCollapsedItemIndex = Math.min(...collapsedItemIndexes, children.length > MAX_VISIBLE_ITEMS ? 1 : Infinity);
        const _lastCollapsedItemIndex = Math.max(...collapsedItemIndexes, children.length > MAX_VISIBLE_ITEMS ? children.length - (MAX_VISIBLE_ITEMS - 1) : -Infinity);
        if (Number.isFinite(_firstCollapsedItemIndex) && Number.isFinite(_lastCollapsedItemIndex)) {
            setFirstCollapsedItemIndex(_firstCollapsedItemIndex);
            setLastCollapsedItemIndex(_lastCollapsedItemIndex);
        }
        else {
            setFirstCollapsedItemIndex(null);
            setLastCollapsedItemIndex(null);
            setExpandCollapsedItems(false);
        }
    }, 50), [children.length]);
    useLayoutEffect(checkIfItemsCollapsed);
    useResize(checkIfItemsCollapsed);
    const renderCollapsedItems = (itemClassName, typography) => {
        if (firstCollapsedItemIndex === null || lastCollapsedItemIndex === null) {
            return null;
        }
        return arrayFromRange(firstCollapsedItemIndex, Math.min(lastCollapsedItemIndex, children.length - 1)).map((index) => {
            const { onClick, Element = 'a', children: child, ...props } = children[index].props;
            return (jsx("div", { className: itemClassName, children: jsx(Card, { ...props, Element: Element, borderRadius: 12, padding: 8, paddingLeft: 12, paddingRight: 12, style: "primary", hoverStyle: "secondary", hoverEnabled: true, pressEnabled: true, stretched: true, children: jsx("div", { className: styles.interactiveText, children: jsx(Text, { Element: "div", typography: typography, children: jsx("div", { className: styles.textWithEllipsis, children: child }) }) }) }) }, makeItemKey(children, index)));
        });
    };
    const renderLastItem = (visible) => {
        if (children.length === 0) {
            return null;
        }
        const index = children.length - 1;
        const { children: child, href, ...props } = { ...children[index].props };
        return (jsx("div", { className: classnames(styles.item, { [styles.itemAfterEllipsis]: index <= 1 }), ref: visible ? itemRefCallbacks[index] : undefined, children: jsx("div", { className: styles.staticText, children: jsx(Text, { ...(visible ? props : {}), Element: "span", typography: TEXT_TYPOGRAPHY, children: child }) }) }, makeItemKey(children, index)));
    };
    return (jsx("div", { className: styles.breadcrumbs, "data-qa": dataQa, ref: containerRef, children: jsxs("div", { className: styles.unbreakableContainer, children: [children.length > 1 && (jsxs("div", { className: classnames(styles.item, styles.firstItem), ref: itemRefCallbacks[0], children: [jsx("div", { className: styles.firstItemContent, children: cloneElement(children[0], undefined, jsx("div", { className: styles.textWithEllipsis, children: children[0].props.children })) }), jsx(BreadcrumbsDivider, {})] })), jsxs("div", { className: classnames({ [styles.invisibleContainer]: !showEllipsis }), "data-qa": "breadcrumbs-action", children: [jsx("button", { type: "button", className: styles.ellipsis, ref: activatorRef, onClick: () => setExpandCollapsedItems(!expandCollapsedItems), children: jsx("div", { className: styles.interactiveText, children: jsx(Text, { Element: "span", typography: TEXT_TYPOGRAPHY, children: "..." }) }) }), jsx(BreadcrumbsDivider, {}), showEllipsis &&
                            (isMobile ? (jsx(BottomSheet, { visible: expandCollapsedItems, onClose: () => setExpandCollapsedItems(false), children: renderCollapsedItems(styles.bottomSheetItem, 'label-2-regular') })) : (jsx(Drop, { activatorRef: activatorRef, placement: "bottom-left", space: 300, visible: expandCollapsedItems, onClose: () => setExpandCollapsedItems(false), children: renderCollapsedItems(styles.dropItem, TEXT_TYPOGRAPHY) })))] }), jsxs("div", { className: styles.collapsibleContainer, children: [jsx("div", { className: styles.invisibleContainer, children: renderLastItem(false) }), jsxs("div", { className: styles.collapsibleContent, children: [renderLastItem(true), arrayFromRange(children.length <= MAX_VISIBLE_ITEMS ? 1 : children.length - (MAX_VISIBLE_ITEMS - 2), children.length - 2)
                                    .reverse()
                                    .map((index) => (jsxs("div", { className: classnames(styles.item, { [styles.itemAfterEllipsis]: index <= 1 }), ref: itemRefCallbacks[index], children: [children[index], jsx(BreadcrumbsDivider, {})] }, makeItemKey(children, index))))] })] })] }) }));
};

export { Breadcrumbs };
//# sourceMappingURL=Breadcrumbs.js.map
